import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { getAgingStatusColor } from '../../utils/unitDetailHelpers';
import AITab from './UnitDetailPane/AITab';
import OverviewTab from './UnitDetailPane/OverviewTab';
import PhotosTab from './UnitDetailPane/PhotosTab';
import PricingTab from './UnitDetailPane/PricingTab';
import PipelineTab from './UnitDetailPane/PipelineTab';
import CompsTab from './UnitDetailPane/CompsTab';
import ServiceHistoryTab from './UnitDetailPane/ServiceHistoryTab';
import FastTrackAgentTab from './UnitDetailPane/FastTrackAgentTab';
import ActivityTab from './UnitDetailPane/ActivityTab';
import useUnitDetail from '../../hooks/useUnitDetail';
import { unitAgingBucketId } from '../../utils/dealershipSettings';
import { useDealershipSettings } from '../../contexts/settingsContext';
import './UnitDetailPane.css';

function UnitDetailPane({ unit, onClose, initialTab, openCreateTask = false }) {
    const [activeTab, setActiveTab] = useState(initialTab || 'ai');
    const { aging } = useDealershipSettings();

    // Update active tab when initialTab changes (e.g. clicking different column)
    useEffect(() => {
        if (initialTab) setActiveTab(initialTab);
    }, [initialTab, unit.StockNumber]);

    // "Create task" opens the create form once: leaving the Activity tab
    // forgets it, so coming back does not pop the form up again.
    const [pendingCreate, setPendingCreate] = useState(openCreateTask);
    useEffect(() => { setPendingCreate(openCreateTask); }, [openCreateTask, unit.StockNumber]);
    useEffect(() => {
        if (activeTab !== 'activity') setPendingCreate(false);
    }, [activeTab]);

    // Every load for this unit (eager, lazy per tab, the Ask IronSight
    // prefetch) and the calculator price: hooks/useUnitDetail, shared with the
    // full unit page (UnitV2) so the two cannot drift.
    const detail = useUnitDetail(unit, activeTab);
    const {
        mfData, mfLoading, images, imagesLoading, pipeline, pipelineLoading, quoteEquipment, quoteEquipmentLoading,
        serviceHistory, serviceHistoryLoading, priceHistory, priceHistoryLoading, priceHistoryError,
        fastTrackAgent, fastTrackLoading, fastTrackError, predictions12m, predictions12mLoading,
        comps, compsLoading, calcPrice, setCalcPrice,
    } = detail;

    const [lightboxIndex, setLightboxIndex] = useState(null);
    // A new unit starts with the lightbox closed (P-UNIT-016).
    useEffect(() => { setLightboxIndex(null); }, [unit.StockNumber]);

    const paneRef = useRef(null);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (lightboxIndex !== null) {
                if (event.key === 'ArrowRight') setLightboxIndex((idx) => Math.min(idx + 1, images.length - 1));
                else if (event.key === 'ArrowLeft') setLightboxIndex((idx) => Math.max(idx - 1, 0));
                else if (event.key === 'Escape') setLightboxIndex(null);
            } else if (event.key === 'Escape') {
                onClose();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [images.length, lightboxIndex, onClose]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (lightboxIndex !== null) return;
            // Overlays that belong to this pane but render through a portal (the
            // comps photo/quote popovers) sit outside paneRef in the DOM. Without
            // this, clicking one would read as a click outside and close the pane.
            if (event.target.closest?.('[data-pane-overlay]')) return;
            if (paneRef.current && !paneRef.current.contains(event.target)) {
                onClose();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [lightboxIndex, onClose]);

    if (!unit) return null;

    const tabs = [
        { id: 'ai', label: 'Intelligence' },
        { id: 'fasttrack', label: 'Ask IronSight' },
        { id: 'overview', label: 'Overview' },
        { id: 'service', label: 'Service History' },
        { id: 'photos', label: 'Photos' },
        { id: 'pricing', label: 'Pricing & Usage' },
        { id: 'pipeline', label: 'Pipeline' },
        { id: 'comps', label: 'Comps' },
        { id: 'activity', label: 'Activity' },
    ];

    return (
        <div className="detail-pane-overlay">
            <div className="detail-pane" ref={paneRef}>
                <div className="detail-header">
                    <div className="header-title">
                        <div
                            className="unit-badge"
                            style={{
                                backgroundColor: `${getAgingStatusColor(unitAgingBucketId(unit, aging))}20`,
                                borderColor: getAgingStatusColor(unitAgingBucketId(unit, aging)),
                            }}
                        >
                            <span className="unit-badge-dot" style={{ backgroundColor: getAgingStatusColor(unitAgingBucketId(unit, aging)) }} />
                        </div>
                        <div>
                            <h2>Stock #{unit.StockNumber}</h2>
                            <div className="unit-subtitle-row">
                                <p className="unit-subtitle">{unit.MAKE} {unit.MODEL} {unit.Model_Year && `(${unit.Model_Year})`}</p>
                                {unit.StockStatus === 'P' && (
                                    <span className="stock-status-badge stock-status-pending">Pending</span>
                                )}
                                {unit.StockStatus === 'S' && (
                                    <span className="stock-status-badge stock-status-instock">In Stock</span>
                                )}
                            </div>
                        </div>
                    </div>
                    <button onClick={onClose} className="btn-close" aria-label="Close details">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>

                <div className="detail-tabs">
                    {tabs.map((tab) => (
                        <button
                            key={tab.id}
                            className={`detail-tab ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                <div className="detail-content">
                    {activeTab === 'ai' && (
                        <AITab
                            unit={unit}
                            mfData={mfData}
                            mfLoading={mfLoading}
                            calcPrice={calcPrice}
                            setCalcPrice={setCalcPrice}
                            onSwitchTab={setActiveTab}
                            comps={comps}
                            pipeline={pipeline}
                            quoteEquipment={quoteEquipment}
                            predictions12m={predictions12m}
                            predictions12mLoading={predictions12mLoading}
                        />
                    )}

                    {activeTab === 'fasttrack' && (
                        <FastTrackAgentTab
                            agent={fastTrackAgent}
                            loading={fastTrackLoading}
                            error={fastTrackError}
                            unit={unit}
                            onRegenerate={detail.regenerateAgent}
                        />
                    )}

                    {activeTab === 'overview' && (
                        <OverviewTab unit={unit} mfData={mfData} mfLoading={mfLoading} />
                    )}

                    <div style={{ display: activeTab === 'service' ? 'contents' : 'none' }}>
                        {/* Keyed by unit: its brief, chat and selected work order
                            belong to one unit and must not carry over (P-UNIT-016). */}
                        <ServiceHistoryTab
                            key={unit.StockNumber}
                            serviceHistory={serviceHistory}
                            loading={serviceHistoryLoading}
                            stockNumber={unit.StockNumber}
                            unit={unit}
                            mfOptions={mfData?.options || []}
                            onRegenerate={detail.refreshServiceHistory}
                        />
                    </div>

                    {activeTab === 'photos' && (
                        <PhotosTab
                            unit={unit}
                            images={images}
                            imagesLoading={imagesLoading}
                            setLightboxIndex={setLightboxIndex}
                        />
                    )}

                    {activeTab === 'pricing' && (
                        <PricingTab
                            unit={unit}
                            priceHistory={priceHistory}
                            priceHistoryLoading={priceHistoryLoading}
                            priceHistoryError={priceHistoryError}
                        />
                    )}

                    {activeTab === 'pipeline' && (
                        <PipelineTab
                            unit={unit}
                            pipeline={pipeline}
                            pipelineLoading={pipelineLoading}
                            quoteEquipment={quoteEquipment}
                            quoteEquipmentLoading={quoteEquipmentLoading}
                        />
                    )}

                    {activeTab === 'comps' && (
                        <div className="tab-content">
                            <CompsTab unit={unit} comps={comps} compsLoading={compsLoading} />
                        </div>
                    )}

                    {activeTab === 'activity' && (
                        <ActivityTab unit={unit} openCreate={pendingCreate} />
                    )}
                </div>
            </div>

            {lightboxIndex !== null && (
                <div className="lightbox-overlay" onClick={() => setLightboxIndex(null)}>
                    <button className="lightbox-close" onClick={() => setLightboxIndex(null)} aria-label="Close">
                        <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                    {lightboxIndex > 0 && (
                        <button className="lightbox-nav lightbox-prev" onClick={(event) => { event.stopPropagation(); setLightboxIndex(lightboxIndex - 1); }} aria-label="Previous">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                    <img
                        src={images[lightboxIndex]}
                        alt={`${unit.MAKE} ${unit.MODEL} — view ${lightboxIndex + 1}`}
                        className="lightbox-img"
                        onClick={(event) => event.stopPropagation()}
                    />
                    {lightboxIndex < images.length - 1 && (
                        <button className="lightbox-nav lightbox-next" onClick={(event) => { event.stopPropagation(); setLightboxIndex(lightboxIndex + 1); }} aria-label="Next">
                            <svg viewBox="0 0 20 20" fill="currentColor" width="20" height="20">
                                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                            </svg>
                        </button>
                    )}
                    <span className="lightbox-counter">{lightboxIndex + 1} / {images.length}</span>
                </div>
            )}
        </div>
    );
}

UnitDetailPane.propTypes = {
    unit: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    initialTab: PropTypes.string,
    // Open the Activity tab's create-request form (the inventory's "Create task").
    openCreateTask: PropTypes.bool,
};

export default UnitDetailPane;
